import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Pagination, Spin, Empty, Alert } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { adminService, Content } from '../services/admin';

const { Search } = Input;

interface ContentSelectorMultiProps {
  onSelect?: (fileIds: string[], contents: Content[]) => void;
  disabled?: boolean;
  loading?: boolean;
  multipleSelectionLimit?: number;
  selectedItems?: Content[];
}

const ContentSelectorMulti: React.FC<ContentSelectorMultiProps> = ({
  onSelect,
  disabled = false,
  loading = false,
  multipleSelectionLimit = 50,
  selectedItems = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(5); // 5 items per page as requested
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    selectedItems.map(item => item.fileId)
  );
  const [selectedContents, setSelectedContents] = useState<Content[]>(selectedItems);

  // Fetch content data with pagination and search
  const { data, isLoading } = useQuery({
    queryKey: ['contents-modal-multi', { page, limit, filename: searchTerm }],
    queryFn: () => adminService.getContents({
      page,
      limit,
      ...(searchTerm && { filename: searchTerm }),
    }),
    placeholderData: (previousData) => previousData,
  });

  // Notify parent when selectedItems prop changes
  useEffect(() => {
    if (selectedItems && selectedItems.length > 0) {
      const fileIds = selectedItems.map(item => item.fileId);
      if (onSelect) {
        onSelect(fileIds, selectedItems);
      }
    }
  }, []);

  // Handle pagination change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: Content[]) => {
      // Check if we're at limit
      if (selectedRows.length > multipleSelectionLimit) {
        // Keep only up to limit
        const limitedSelection = selectedRows.slice(0, multipleSelectionLimit);
        setSelectedRowKeys(limitedSelection.map(item => item.fileId));
        setSelectedContents(limitedSelection);

        // Notify parent
        if (onSelect) {
          const fileIds = limitedSelection.map(item => item.fileId);
          onSelect(fileIds, limitedSelection);
        }
        return;
      }

      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedContents(selectedRows);

      // Notify parent component immediately
      if (onSelect) {
        const fileIds = newSelectedRowKeys as string[];
        onSelect(fileIds, selectedRows);
      }
    },
    onSelectAll: (selected: boolean, selectedData: Content[], changeData: Content[]) => {
      if (selected) {
        // Prevent exceeding limit
        if (selectedContents.length + changeData.length > multipleSelectionLimit) {
          const availableSlots = multipleSelectionLimit - selectedContents.length;
          const limitedRows = changeData.slice(0, availableSlots);
          const newList = [...selectedContents, ...limitedRows];
          setSelectedContents(newList);
          setSelectedRowKeys(newList.map(item => item.fileId));

          if (onSelect) {
            const fileIds = newList.map(item => item.fileId);
            onSelect(fileIds, newList);
          }
          return;
        }
        setSelectedContents(selectedData);
        setSelectedRowKeys(selectedData.map(item => item.fileId));

        if (onSelect) {
          const fileIds = selectedData.map(item => item.fileId);
          onSelect(fileIds, selectedData);
        }
      } else {
        // Remove only the current page items from selection
        const remainingContents = selectedContents.filter(
          item => !changeData.some(row => row.fileId === item.fileId)
        );
        setSelectedContents(remainingContents);
        setSelectedRowKeys(remainingContents.map(item => item.fileId));

        if (onSelect) {
          const fileIds = remainingContents.map(item => item.fileId);
          onSelect(fileIds, remainingContents);
        }
      }
    },
    getCheckboxProps: (record: Content) => ({
      disabled: selectedContents.length >= multipleSelectionLimit && !selectedContents.some(item => item.fileId === record.fileId),
    }),
  };

  // Define table columns
  const columns = [
    {
      title: 'File Name',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
      render: (filename: string, record: Content) => (
        <div>
          <div style={{ fontWeight: 500 }}>{filename}</div>
          <div style={{ fontSize: '11px', color: '#666', fontFamily: 'monospace' }}>
            ID: {record.fileId}
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 80,
      render: (type: string) => (
        <span style={{ textTransform: 'capitalize' }}>{type}</span>
      ),
    },
    {
      title: 'Resolution',
      dataIndex: 'resolution',
      key: 'resolution',
      width: 100,
      render: (resolution: string) => resolution || '-',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Selection Info */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#666' }}>
            Selected: {selectedContents.length} / {multipleSelectionLimit} items
          </div>
          {selectedContents.length > 0 && (
            <Button size="small" onClick={() => {
              setSelectedRowKeys([]);
              setSelectedContents([]);

              // Notify parent about clear
              if (onSelect) {
                onSelect([], []);
              }
            }}>
              Clear Selection
            </Button>
          )}
        </div>

        {/* Search Input */}
        <Search
          placeholder="Search by filename..."
          allowClear
          enterButton={<SearchOutlined />}
          size="middle"
          onSearch={handleSearch}
          onChange={(e) => e.target.value === '' && handleSearch('')}
          loading={isLoading}
          disabled={disabled}
        />

        {/* Content Table */}
        <Spin spinning={loading || isLoading}>
          <Table
            rowKey="fileId"
            size="small"
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={data?.data || []}
            pagination={false}
            rowSelection={rowSelection}
            locale={{
              emptyText: searchTerm ? (
                <Empty description={`No content found for "${searchTerm}"`} />
              ) : (
                <Empty description="No content available" />
              ),
            }}
            style={{
              minHeight: '300px',
            }}
          />

          {/* Pagination */}
          {data && data.total > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px' }}>
              <div style={{ color: '#666' }}>
                Page {page} of {data.totalPages}
              </div>
              <Pagination
                current={page}
                total={data.total}
                pageSize={limit}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                onChange={handlePageChange}
                size="small"
                disabled={disabled}
              />
            </div>
          )}
        </Spin>

        {/* Selection limit warning */}
        {selectedContents.length >= multipleSelectionLimit && (
          <Alert
            message="Selection limit reached"
            description={`You have selected the maximum of ${multipleSelectionLimit} items. Please deselect some items if you want to add more.`}
            type="warning"
            showIcon
          />
        )}
      </Space>
    </div>
  );
};

export default ContentSelectorMulti;